import {AwRoadmapTimeline, AwErrorState, AwLoadingState, AwPanel, AwSelect} from '@ascension-tracker/ascension-ui';
import {useState} from 'react';
import {useProjectDashboard, useProjectSections, useProjectTasks, useRescheduleTask, type Task} from '../../hooks/useTasks.js';

function dateOnly(value: string | null | undefined) {
  return value ? value.slice(0, 10) : undefined;
}

export function TimelineView({projectId, projectKey, onOpenTask}: {projectId: string; projectKey: string; onOpenTask: (taskId: string) => void}) {
  const sections = useProjectSections(projectId);
  const tasks = useProjectTasks(projectId);
  const dashboard = useProjectDashboard(projectKey);
  const reschedule = useRescheduleTask(projectId);
  const [scale, setScale] = useState<'monthly' | 'quarterly' | 'yearly'>('quarterly');

  const error = (sections.error as Error | null)?.message || (tasks.error as Error | null)?.message;
  if (error) return <AwErrorState>{error}</AwErrorState>;
  if (!sections.data || !tasks.data) return <AwLoadingState/>;

  const sectionName = (sectionId: string | null) => sections.data!.find(section => section.id === sectionId)?.name ?? 'No section';
  const scheduled = tasks.data.filter(task => task.startOn || task.dueOn);
  const unscheduled = tasks.data.filter(task => !task.startOn && !task.dueOn);

  const taskItems = scheduled.map(task => ({
    id: task.id,
    title: task.title,
    status: task.completedAt ? 'Completed' : 'Planned',
    startDate: dateOnly(task.startOn),
    desiredDate: dateOnly(task.dueOn) ?? dateOnly(task.startOn),
    projectName: sectionName(task.sectionId)
  }));

  const milestoneItems = (dashboard.data?.milestones ?? [])
    .filter(milestone => milestone.startDate || milestone.targetDate)
    .map(milestone => ({
      id: `milestone-${milestone.id}`,
      title: `🏁 ${milestone.name}`,
      status: milestone.status,
      startDate: dateOnly(milestone.startDate) ?? dateOnly(milestone.targetDate),
      desiredDate: dateOnly(milestone.targetDate) ?? dateOnly(milestone.startDate),
      projectName: 'Milestone'
    }));

  const gateItems = (dashboard.data?.stages ?? [])
    .filter(stage => stage.completedAt)
    .map(stage => ({
      id: `gate-${stage.environment}`,
      title: `◇ ${stage.environment.replace('_', ' ')} gate`,
      status: 'Completed',
      startDate: dateOnly(stage.completedAt),
      desiredDate: dateOnly(stage.completedAt),
      projectName: 'Release Gate'
    }));

  const items = [...taskItems, ...milestoneItems, ...gateItems];

  function onSelect(item: {id: string}) {
    if (item.id.startsWith('milestone-') || item.id.startsWith('gate-')) return;
    onOpenTask(item.id);
  }
  function onMove(item: {id: string}, dates: {startDate: string; desiredDate: string}) {
    if (item.id.startsWith('milestone-') || item.id.startsWith('gate-')) return;
    reschedule.mutate({taskId: item.id, startOn: dates.startDate, dueOn: dates.desiredDate});
  }

  return (
    <div className="timeline-view">
      <div className="timeline-view__controls">
        <AwSelect id="timeline-scale" label="Scale" value={scale} onChange={event => setScale(event.target.value as typeof scale)}>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="yearly">Yearly</option>
        </AwSelect>
      </div>
      {items.length === 0 ? (
        <AwPanel><p className="task-detail__muted">No tasks have a start or due date yet. Set dates from the task detail panel to see them on the timeline.</p></AwPanel>
      ) : (
        <AwPanel className="timeline-view__panel">
          <AwRoadmapTimeline items={items} scale={scale} onSelect={onSelect} onMove={onMove}/>
        </AwPanel>
      )}
      {unscheduled.length > 0 && (
        <AwPanel className="timeline-view__unscheduled">
          <h2>Unscheduled</h2>
          {unscheduled.map(task => <UnscheduledRow key={task.id} task={task} onOpen={() => onOpenTask(task.id)}/>)}
        </AwPanel>
      )}
    </div>
  );
}

function UnscheduledRow({task, onOpen}: {task: Task; onOpen: () => void}) {
  return (
    <button type="button" className="timeline-view__unscheduled-row" onClick={onOpen}>{task.title}</button>
  );
}
